import { forwardRef, useState } from 'react';
import {
  ApiManualSensor,
  ApiReturnDeviceType,
  ApiReturnDeviceTypeMeasureType,
  ApiReturnManualSensor,
  ApiReturnMeasureType,
} from '@highimpact-frontend/api-types';
import { resetExternalState } from '../../../../store/data/slice';
import { useAppDispatch } from '../../../../store/hooks';
import DeviceService from '../../../../api/services/config/device/DeviceService';
import { createValidator, useForm } from '../../../../utils/hooks/useForm';
import { getMeasureTypesFromDeviceType } from '../../../../store/config';
import { DataState } from '../../../../store/data/state';
import DropdownStringSelector from '../../../utils/MUISpecifics/dropDownStringSelector/DropDownStringSelector';
import { getEntityById, recordNotEqual } from '../../../../utils/functions';
import CustomizedDialog from '../../../utils/MUISpecifics/dialog/CustumizedDialog';
import { useSubmit } from '../../../../utils/hooks/useSubmit';
import { Button, Stack } from '@mui/material';
import TextInput from '../../../utils/MUISpecifics/inputText/TextInput';
import DropdownItemsSelector from '../../../utils/MUISpecifics/dropDownItemSelector/DropDownItemSelector';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';

interface UpdateManualDevicePorps {
  onCancel: () => void;
  isOpen: boolean;
  closeModal: () => void;
  deviceTypeMeasureTypes: DataState<
    string,
    { [KEY in number]: ApiReturnDeviceTypeMeasureType }
  >;
  device_types: ApiReturnDeviceType[];
  measureTypes: DataState<string, { [KEY in number]: ApiReturnMeasureType }>;
  models: string[];
  manualSensor: ApiReturnManualSensor;
}

function UpdateManualDevice({
  onCancel,
  isOpen,
  closeModal,
  deviceTypeMeasureTypes,
  device_types,
  measureTypes,
  models,
  manualSensor,
}: UpdateManualDevicePorps): JSX.Element {
  const { t } = useTranslation('components.devices');

  const dispatch = useAppDispatch();
  const { submit } = useSubmit();
  const deviceType = manualSensor
    ? getEntityById(manualSensor.device_type_id!, device_types)
    : null;
  const [filtredProposition, setFiltredProposition] = useState<{
    deviceType: ApiReturnDeviceType | null;
    measureTypes: ApiReturnMeasureType[];
  }>({
    deviceType: null,
    measureTypes: getMeasureTypesFromDeviceType(
      deviceType!,
      deviceTypeMeasureTypes,
      measureTypes
    ),
  });

  const defaultValues: ApiManualSensor = {
    name: manualSensor.name,
    brand: manualSensor.brand,
    type: 'manual_sensor',
    device_type_id: manualSensor.device_type_id,
    sn: manualSensor.sn,
  };

  const updateMeasures = (id: number) => {
    if (id !== null) {
      const deviceType = getEntityById(id, device_types);
      const measures = getMeasureTypesFromDeviceType(
        deviceType!,
        deviceTypeMeasureTypes,
        measureTypes
      );
      methods.setValue('device_type_id', id);
      setFiltredProposition({
        deviceType: deviceType!,
        measureTypes: measures,
      });
    } else {
      methods.setValue('device_type_id', defaultValues.device_type_id);
      setFiltredProposition({ deviceType: null, measureTypes: [] });
    }
  };

  /**
   * Update validation rule for each field.
   */
  const validator = createValidator<ApiManualSensor>({
    name: [
      (n) =>
        n.length < 3 || !n.trim().length
          ? 'Entrer au moins 3 caractères valides'
          : undefined,
    ],
    brand: [
      (n) =>
        n === '' || n === null || n === undefined || !n.trim().length
          ? t('Choisir un modèle ou entrer des caractères valides')
          : undefined,
    ],
    type: [
      (n) =>
        (n && n.length <= 3) || (n && !n.trim().length)
          ? t('Entrer au moins 4 caractères')
          : undefined,
    ],
    sn: [
      (n) => (n && n.length === 0 ? 'Entrer au moins 1 caractères' : undefined),
    ],
    device_type_id: [
      (n) => (n === null ? t('Choisir un type de capteur') : undefined),
    ],
  });

  const onSubmit = async (data: ApiManualSensor) => {
    const measureTypesToUpdate = filtredProposition.measureTypes
      ? filtredProposition.measureTypes.map((mt) => mt.id)
      : [];

    submit({
      callbacks: [
        async () => await DeviceService.update(manualSensor.id, data),
        async () =>
          await DeviceService.UpdateDeviceMeasureTypesAssociation(
            measureTypesToUpdate,
            manualSensor.id
          ),
        () => dispatch(resetExternalState({ external_key: 'devices' })),
        () => dispatch(resetExternalState({ external_key: 'site_devices' })),
        () => dispatch(resetExternalState({ external_key: 'sites' })),
        () => closeModal(),
      ],
      errorMessages: {
        anyError: t(
          'Une erreur est survenue lors de la modification du dispositif de mesure.'
        ),
        unicityError: t(
          'Un dispositif de mesure avec le même nom, modèle et numéro de série existe déjà'
        ),
      },
      successMessage: t('Le dispositif de mesure a été modifié avec succès'),
    });
  };

  // Define form hook.
  const methods = useForm(defaultValues, validator, onSubmit);

  return (
    <div className="customized-dialog-wrapper">
      <CustomizedDialog
        title={t('Modifier le dispositif de mesure')}
        open={isOpen}
        onCancel={onCancel}
      >
        <form onSubmit={methods.onSubmit} className={`form`}>
          <Stack spacing={2}>
            <TextInput
              onChange={(value) => methods.setValue('name', value)}
              label={t('Nom')}
              name={'name'}
              placeholder={t('Entrer un nom...')}
              defaultValue={methods.watch('name')}
              required
              errors={
                methods.hasSubmitted
                  ? { tag: 'Submited', errors: methods.getErrors('name') }
                  : { tag: 'Not submitted' }
              }
            />

            <DropdownItemsSelector
              defaultValue={methods.watch('device_type_id')}
              onSelect={(value) => updateMeasures(value!)}
              values={device_types}
              label={t('Type de dispositif de mesure')}
              disabled={false}
              name={'device_type_id'}
              required
              errors={
                methods.hasSubmitted
                  ? {
                      tag: 'Submited',
                      errors: methods.getErrors('device_type_id'),
                    }
                  : { tag: 'Not submitted' }
              }
            />

            {filtredProposition.measureTypes.length !== 0 && (
              <div
                className="uk-margin"
                style={{
                  backgroundColor: '#00000008',
                  borderRadius: '5px',
                  padding: '15px',
                }}
              >
                <div>Types de mesures</div>
                <div
                  className="uk-margin"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                  }}
                >
                  {filtredProposition.measureTypes.map((mt) => (
                    <div
                      key={mt.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'start',
                        gap: '10px',
                      }}
                    >
                      <span
                        style={{
                          backgroundColor: '#d3f7b0',
                          padding: '5px',
                          borderRadius: '5px',
                        }}
                      >
                        {mt.name}
                      </span>
                      <span
                        style={{
                          backgroundColor: '#f7b0ba',
                          padding: '5px',
                          borderRadius: '5px',
                        }}
                      >
                        {mt.unite}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <DropdownStringSelector
              defaultValue={methods.watch('brand')}
              onSelect={(value) => methods.setValue('brand', value)}
              values={models}
              label={t('Modèle')}
              disabled={false}
              name={'brand'}
              required
              errors={
                methods.hasSubmitted
                  ? { tag: 'Submited', errors: methods.getErrors('brand') }
                  : { tag: 'Not submitted' }
              }
            />

            <TextInput
              defaultValue={methods.watch('sn')!}
              onChange={(value) => methods.setValue('sn', value)}
              label={t('Numéro de série')}
              name={'sn'}
              placeholder={t('Entrer un numero de serie...')}
              required
              errors={
                methods.hasSubmitted
                  ? { tag: 'Submited', errors: methods.getErrors('sn') }
                  : { tag: 'Not submitted' }
              }
            />

            <Button
              variant="outlined"
              type="submit"
              disabled={!recordNotEqual(defaultValues, methods.watchAll())}
              className="form-button-submit"
              loadingPosition="end"
              endIcon={<SaveIcon />}
            >
              {t('Modifier')}
            </Button>
          </Stack>
        </form>
      </CustomizedDialog>
    </div>
  );
}

export default forwardRef(UpdateManualDevice);
